import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  TableCellsIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  PhotoIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';

const pdfStyles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#1e293b',
  },
  header: {
    marginBottom: 14,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: '#dc2626',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#991b1b',
  },
  subtitle: {
    fontSize: 8,
    color: '#64748b',
    marginTop: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  summaryCard: {
    flex: 1,
    padding: 8,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 4,
  },
  summaryLabel: {
    fontSize: 7,
    color: '#64748b',
  },
  summaryVal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0f172a',
    marginTop: 2,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#991b1b',
    color: '#ffffff',
    paddingVertical: 5,
    paddingHorizontal: 4,
    fontWeight: 'bold',
    fontSize: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    paddingVertical: 5,
    paddingHorizontal: 4,
    fontSize: 8,
  },
  colReport: { width: '9%' },
  colDate: { width: '11%' },
  colClient: { width: '16%' },
  colEquip: { width: '16%' },
  colPart: { width: '24%' },
  colSeverity: { width: '9%' },
  colDetail: { width: '15%' },
});

function DamagedPartsPdfDocument({ items, generatedAt }) {
  const criticalCount = items.filter((i) => i.severity === 'Malo').length;
  const regularCount = items.filter((i) => i.severity === 'Regular').length;
  const affectedEquipments = new Set(
    items.map((i) => i.project_id || i.equipment_name).filter(Boolean)
  ).size;

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <View>
            <Text style={pdfStyles.title}>INFORME DE PARTES DAÑADAS DE EQUIPOS</Text>
            <Text style={pdfStyles.subtitle}>
              Partes calificadas como Malo o Regular que aún no han sido marcadas como Buenas en mantenimientos posteriores
            </Text>
          </View>
          <View>
            <Text style={pdfStyles.subtitle}>Generado: {generatedAt}</Text>
            <Text style={pdfStyles.subtitle}>Total registros: {items.length}</Text>
          </View>
        </View>

        <View style={pdfStyles.summaryRow}>
          <View style={pdfStyles.summaryCard}>
            <Text style={pdfStyles.summaryLabel}>Total Partes Sin Corregir</Text>
            <Text style={pdfStyles.summaryVal}>{items.length}</Text>
          </View>
          <View style={pdfStyles.summaryCard}>
            <Text style={pdfStyles.summaryLabel}>Estado Malo</Text>
            <Text style={pdfStyles.summaryVal}>{criticalCount}</Text>
          </View>
          <View style={pdfStyles.summaryCard}>
            <Text style={pdfStyles.summaryLabel}>Estado Regular</Text>
            <Text style={pdfStyles.summaryVal}>{regularCount}</Text>
          </View>
          <View style={pdfStyles.summaryCard}>
            <Text style={pdfStyles.summaryLabel}>Equipos Afectados</Text>
            <Text style={pdfStyles.summaryVal}>{affectedEquipments}</Text>
          </View>
        </View>

        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.colReport}># Rep.</Text>
          <Text style={pdfStyles.colDate}>Fecha</Text>
          <Text style={pdfStyles.colClient}>Cliente</Text>
          <Text style={pdfStyles.colEquip}>Equipo</Text>
          <Text style={pdfStyles.colPart}>Grupo — Parte / Pregunta</Text>
          <Text style={pdfStyles.colSeverity}>Estado</Text>
          <Text style={pdfStyles.colDetail}>Detalle / Técnico</Text>
        </View>

        {items.map((item, idx) => {
          const currDateStr = item.date
            ? new Date(item.date).toLocaleDateString('es-CO')
            : '—';

          return (
            <View
              key={item.id || idx}
              style={[
                pdfStyles.tableRow,
                idx % 2 === 1 ? { backgroundColor: '#f8fafc' } : {},
              ]}
            >
              <Text style={pdfStyles.colReport}>#{item.report_id}</Text>
              <Text style={pdfStyles.colDate}>{currDateStr}</Text>
              <Text style={pdfStyles.colClient}>
                {item.client_name || item.customer_name || '—'}
              </Text>
              <Text style={pdfStyles.colEquip}>{item.equipment_name || '—'}</Text>
              <Text style={pdfStyles.colPart}>
                {item.part_or_question || item.part_or_item || '—'}
              </Text>
              <Text style={pdfStyles.colSeverity}>{item.severity || '—'}</Text>
              <Text style={pdfStyles.colDetail}>
                {item.detail || item.observation || ''} ({item.technician_name || '—'})
              </Text>
            </View>
          );
        })}
      </Page>
    </Document>
  );
}

export default function InformePartesDanadas() {
  const company = sessionStorage.getItem('company');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Photo preview modal
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/getDamagedPartsReport/${company}`);
      setItems(res.data?.data || []);
    } catch (error) {
      console.error('Error loading damaged parts report:', error);
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const uniqueClients = useMemo(
    () =>
      Array.from(
        new Set(
          items
            .map((i) => i.client_name || i.customer_name)
            .filter((v) => v && v !== '—')
        )
      ).sort(),
    [items]
  );

  const uniqueEquipments = useMemo(
    () =>
      Array.from(
        new Set(items.map((i) => i.equipment_name).filter((v) => v && v !== '—'))
      ).sort(),
    [items]
  );

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const cName = item.client_name || item.customer_name || '';
      const pName = item.part_or_question || item.part_or_item || '';
      const dText = item.detail || item.observation || '';

      if (clientFilter && cName !== clientFilter) return false;
      if (equipmentFilter && item.equipment_name !== equipmentFilter) return false;
      if (severityFilter && item.severity !== severityFilter) return false;
      if (startDate && item.date) {
        if (new Date(item.date) < new Date(`${startDate}T00:00:00`)) return false;
      }
      if (endDate && item.date) {
        if (new Date(item.date) > new Date(`${endDate}T23:59:59`)) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const match =
          cName.toLowerCase().includes(q) ||
          (item.equipment_name || '').toLowerCase().includes(q) ||
          pName.toLowerCase().includes(q) ||
          dText.toLowerCase().includes(q) ||
          (item.technician_name || '').toLowerCase().includes(q) ||
          String(item.report_id || '').includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [items, clientFilter, equipmentFilter, severityFilter, startDate, endDate, search]);

  const stats = useMemo(() => {
    return {
      total: filteredItems.length,
      critical: filteredItems.filter((i) => i.severity === 'Malo').length,
      regular: filteredItems.filter((i) => i.severity === 'Regular').length,
      affectedEquipments: new Set(
        filteredItems.map((i) => i.project_id || i.equipment_name).filter(Boolean)
      ).size,
    };
  }, [filteredItems]);

  const handleExportExcel = async () => {
    try {
      setExportingExcel(true);
      const params = new URLSearchParams();
      if (severityFilter) params.set('severity', severityFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const response = await axios.get(
        `/exportDamagedPartsExcel/${company}?${params.toString()}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Partes_Danadas_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting damaged parts Excel:', error);
      alert('No se pudo exportar el informe en Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setExportingPdf(true);
      const generatedAt = new Date().toLocaleString('es-CO');
      const blob = await pdf(
        <DamagedPartsPdfDocument items={filteredItems} generatedAt={generatedAt} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Informe_Partes_Danadas_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating damaged parts PDF:', error);
      alert('No se pudo generar el PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const getSeverityChip = (severity) => {
    switch (severity) {
      case 'Malo':
        return <Chip size="small" label="Malo" color="error" sx={{ fontWeight: 600 }} />;
      case 'Regular':
        return <Chip size="small" label="Regular" color="warning" sx={{ fontWeight: 600 }} />;
      default:
        return <Chip size="small" label={severity || '—'} />;
    }
  };

  const getFullPhotoUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('http')) return path;
    const base = axios.defaults.baseURL || '';
    const clean = path.startsWith('/') ? path.substring(1) : path;
    return `${base.endsWith('/') ? base : `${base}/`}${clean}`;
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Button
            size="small"
            startIcon={<ArrowLeftIcon className="h-4 w-4" />}
            onClick={() => navigate('/mantenimiento/historial')}
            sx={{ mb: 0.5, textTransform: 'none' }}
          >
            Volver al Historial de Mantenimientos
          </Button>
          <Typography variant="h5" fontWeight="bold">
            Informe de Partes Dañadas de Equipos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Partes calificadas como Malo o Regular que aún no han sido corregidas (marcadas como Buenas) en mantenimientos posteriores.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            color="success"
            onClick={handleExportExcel}
            disabled={exportingExcel || filteredItems.length === 0}
            startIcon={<TableCellsIcon className="h-5 w-5" />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDownloadPdf}
            disabled={exportingPdf || filteredItems.length === 0}
            startIcon={<ArrowDownTrayIcon className="h-5 w-5" />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {exportingPdf ? 'Generando PDF...' : 'Descargar PDF'}
          </Button>
        </Stack>
      </Stack>

      {/* Summary KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2.5, borderRadius: 3, borderLeft: '5px solid #3b82f6' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              TOTAL PARTES SIN CORREGIR
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {stats.total}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2.5, borderRadius: 3, borderLeft: '5px solid #dc2626' }}>
            <Typography variant="caption" color="error.main" fontWeight={600}>
              ESTADO MALO
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="error.main">
              {stats.critical}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2.5, borderRadius: 3, borderLeft: '5px solid #f59e0b' }}>
            <Typography variant="caption" color="warning.dark" fontWeight={600}>
              ESTADO REGULAR
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="warning.dark">
              {stats.regular}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2.5, borderRadius: 3, borderLeft: '5px solid #0284c7' }}>
            <Typography variant="caption" color="info.dark" fontWeight={600}>
              EQUIPOS AFECTADOS
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="info.dark">
              {stats.affectedEquipments}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FunnelIcon className="h-5 w-5 text-slate-600" />
          <Typography variant="subtitle2" fontWeight="bold">
            Filtros de Búsqueda
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar parte, cliente, equipo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Cliente"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {uniqueClients.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Equipo"
              value={equipmentFilter}
              onChange={(e) => setEquipmentFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {uniqueEquipments.map((eq) => (
                <MenuItem key={eq} value={eq}>
                  {eq}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Estado"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Malo">Malo</MenuItem>
              <MenuItem value="Regular">Regular</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="Desde"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="Hasta"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Results Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No se encontraron partes dañadas pendientes por corregir con los filtros actuales.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>N° Rep.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Equipo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Parte / Pregunta Dañada</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Detalle / Observación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Técnico</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Fotos
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.map((item, index) => {
                  const currDateFormatted = item.date
                    ? new Date(item.date).toLocaleDateString('es-CO', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })
                    : '—';
                  const partLabel = item.part_or_question || item.part_or_item || '—';

                  return (
                    <TableRow key={item.id || index} hover>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        #{item.report_id}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {currDateFormatted}
                      </TableCell>
                      <TableCell>{item.client_name || item.customer_name || '—'}</TableCell>
                      <TableCell>{item.equipment_name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 280, fontWeight: 500 }}>
                        {partLabel}
                      </TableCell>
                      <TableCell>{getSeverityChip(item.severity)}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {item.detail || item.observation || '—'}
                      </TableCell>
                      <TableCell>{item.technician_name || '—'}</TableCell>
                      <TableCell align="center">
                        {Array.isArray(item.photos) && item.photos.length > 0 ? (
                          <Tooltip title={`Ver ${item.photos.length} foto(s)`}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                setSelectedPhotos(item.photos);
                                setSelectedTitle(partLabel);
                                setPhotoModalOpen(true);
                              }}
                            >
                              <PhotoIcon className="h-5 w-5" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Photo Preview Modal */}
      <Dialog
        open={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Evidencia Fotográfica — {selectedTitle}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {selectedPhotos.map((photo, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Box
                  component="img"
                  src={getFullPhotoUrl(photo)}
                  alt={`Evidencia ${i + 1}`}
                  sx={{
                    width: '100%',
                    maxHeight: 340,
                    objectFit: 'contain',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoModalOpen(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
